import { Injectable } from '@angular/core';
import { Suggestion, SuggestionType } from 'src/app/_models/messaging/messages/rcs-messages/rcs-message.model';

@Injectable({
  providedIn: 'root'
})
export class ValidationService {
  // Common profanity words list (basic set - can be expanded)
  private profanityWords = [
    'damn', 'hell', 'crap', 'shit', 'fuck', 'ass', 'bitch', 'bastard',
    'piss', 'dick', 'cock', 'pussy', 'asshole', 'motherfucker', 'fucking',
    'shitty', 'damned', 'bloody', 'bugger', 'wanker', 'twat', 'cunt'
  ];

  validateUrl(url: string): { valid: boolean; error?: string } {
    if (!url || url.trim() === '') {
      return { valid: true }; // Empty is valid (optional field)
    }

    try {
      const urlObj = new URL(url);
      // Check for valid protocol
      if (!['http:', 'https:'].includes(urlObj.protocol)) {
        return { valid: false, error: 'URL must start with http:// or https://' };
      }
      return { valid: true };
    } catch (e) {
      return { valid: false, error: 'Please enter a valid URL (e.g., https://example.com)' };
    }
  }

  validateEmail(email: string): { valid: boolean; error?: string } {
    if (!email || email.trim() === '') {
      return { valid: true }; // Empty is valid (optional field)
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return { valid: false, error: 'Please enter a valid email address' };
    }
    return { valid: true };
  }

  validatePhone(phone: string): { valid: boolean; error?: string } {
    if (!phone || phone.trim() === '') {
      return { valid: true }; // Empty is valid (optional field)
    }

    // Allow formats like +1234567890, (123) 456-7890, 123-456-7890, etc.
    const phoneRegex = /^[\+]?[(]?[0-9]{1,4}[)]?[-\s\.]?[(]?[0-9]{1,4}[)]?[-\s\.]?[0-9]{1,9}$/;
    if (!phoneRegex.test(phone.replace(/\s/g, ''))) {
      return { valid: false, error: 'Please enter a valid phone number (e.g., +1234567890)' };
    }
    return { valid: true };
  }

  validateTextForProfanity(text: string): { valid: boolean; error?: string; foundWords?: string[] } {
    if (!text || text.trim() === '') {
      return { valid: true };
    }

    const textLower = text.toLowerCase();
    const foundWords: string[] = [];

    this.profanityWords.forEach(word => {
      // Use word boundaries to avoid false positives
      const regex = new RegExp(`\\b${word}\\b`, 'gi');
      if (regex.test(textLower)) {
        foundWords.push(word);
      }
    });

    if (foundWords.length > 0) {
      return {
        valid: false,
        error: 'Text contains inappropriate language. Please remove offensive content.',
        foundWords: [...new Set(foundWords)]
      };
    }

    return { valid: true };
  }

  validateRequired(value: any, fieldName: string): { valid: boolean; error?: string } {
    if (value === null || value === undefined) {
      return { valid: false, error: `${fieldName} is required` };
    }
    if (typeof value === 'string' && value.trim() === '') {
      return { valid: false, error: `${fieldName} is required` };
    }
    if (typeof value === 'number' && isNaN(value)) {
      return { valid: false, error: `${fieldName} is required` };
    }
    return { valid: true };
  }

  validateSuggestion(suggestion: Suggestion): { valid: boolean; errors: { [key: string]: string } } {
    const errors: { [key: string]: string } = {};
    const suggestionType = this.normalizeSuggestionType(suggestion.type);

    const displayTextCheck = this.validateRequired(suggestion.displayText, 'Display Text');
    if (!displayTextCheck.valid) {
      this.setFieldError(errors, 'displayText', displayTextCheck);
    } else {
      const profanityCheck = this.validateTextForProfanity(suggestion.displayText);
      this.setFieldError(errors, 'displayText', profanityCheck);
    }

    switch (suggestionType) {
      case SuggestionType.OPEN_URL: {
        const urlRequired = this.validateRequired(suggestion.url, 'URL');
        if (!urlRequired.valid) {
          this.setFieldError(errors, 'url', urlRequired);
        } else {
          const urlCheck = this.validateUrl(suggestion.url!);
          this.setFieldError(errors, 'url', urlCheck);
        }
        break;
      }
      case SuggestionType.DIAL_PHONE: {
        const phoneRequired = this.validateRequired(suggestion.phone, 'Phone Number');
        if (!phoneRequired.valid) {
          this.setFieldError(errors, 'phone', phoneRequired);
        } else {
          const phoneCheck = this.validatePhone(suggestion.phone!);
          this.setFieldError(errors, 'phone', phoneCheck);
        }
        break;
      }
      case SuggestionType.VIEW_LOCATION: {
        const latRequired = this.validateRequired(suggestion.latitude, 'Latitude');
        if (!latRequired.valid) {
          this.setFieldError(errors, 'latitude', latRequired);
        } else {
          const latCheck = this.validateLatitude(suggestion.latitude);
          this.setFieldError(errors, 'latitude', latCheck);
        }
        const lngRequired = this.validateRequired(suggestion.longitude, 'Longitude');
        if (!lngRequired.valid) {
          this.setFieldError(errors, 'longitude', lngRequired);
        } else {
          const lngCheck = this.validateLongitude(suggestion.longitude);
          this.setFieldError(errors, 'longitude', lngCheck);
        }
        break;
      }
      case SuggestionType.CALENDAR_EVENT: {
        const titleRequired = this.validateRequired(suggestion.meetingTitle, 'Meeting Title');
        if (!titleRequired.valid) {
          this.setFieldError(errors, 'meetingTitle', titleRequired);
        } else {
          const titleProfanityCheck = this.validateTextForProfanity(suggestion.meetingTitle!);
          this.setFieldError(errors, 'meetingTitle', titleProfanityCheck);
        }
        if (suggestion.meetingDescription) {
          const descCheck = this.validateTextForProfanity(suggestion.meetingDescription);
          this.setFieldError(errors, 'meetingDescription', descCheck);
        }
        const startRequired = this.validateRequired(suggestion.startTime, 'Start Time');
        if (!startRequired.valid) {
          this.setFieldError(errors, 'startTime', startRequired);
        } else {
          const startCheck = this.validateDateTime(suggestion.startTime);
          this.setFieldError(errors, 'startTime', startCheck);
        }
        const endRequired = this.validateRequired(suggestion.endTime, 'End Time');
        if (!endRequired.valid) {
          this.setFieldError(errors, 'endTime', endRequired);
        } else {
          const endCheck = this.validateDateTime(suggestion.endTime);
          this.setFieldError(errors, 'endTime', endCheck);
        }
        if (!errors['startTime'] && !errors['endTime'] && suggestion.startTime && suggestion.endTime) {
          const startDate = new Date(suggestion.startTime);
          const endDate = new Date(suggestion.endTime);
          if (endDate <= startDate) {
            errors['endTime'] = 'End time must be after start time';
          }
        }
        break;
      }
    }

    return { valid: Object.keys(errors).length === 0, errors };
  }

  getSuggestionFieldError(suggestion: Suggestion, field: string): string | null {
    const result = this.validateSuggestion(suggestion);
    return result.errors[field] || null;
  }

  private normalizeSuggestionType(type: SuggestionType | string): SuggestionType {
    const values = Object.values(SuggestionType) as string[];
    if (values.includes(type as string)) {
      return type as SuggestionType;
    }
    return SuggestionType.REPLY;
  }

  private setFieldError(
    errors: { [key: string]: string },
    field: string,
    check: { valid: boolean; error?: string }
  ): void {
    if (!check.valid) {
      errors[field] = check.error || '';
    }
  }

  validateLatitude(lat: number | undefined): { valid: boolean; error?: string } {
    if (lat === undefined || lat === null) {
      return { valid: true }; // Optional
    }
    if (isNaN(lat) || lat < -90 || lat > 90) {
      return { valid: false, error: 'Latitude must be between -90 and 90' };
    }
    return { valid: true };
  }

  validateLongitude(lng: number | undefined): { valid: boolean; error?: string } {
    if (lng === undefined || lng === null) {
      return { valid: true }; // Optional
    }
    if (isNaN(lng) || lng < -180 || lng > 180) {
      return { valid: false, error: 'Longitude must be between -180 and 180' };
    }
    return { valid: true };
  }

  validateDateTime(dateTime: string | undefined): { valid: boolean; error?: string } {
    if (!dateTime || dateTime.trim() === '') {
      return { valid: true }; // Optional
    }
    const date = new Date(dateTime);
    if (isNaN(date.getTime())) {
      return { valid: false, error: 'Please enter a valid date and time' };
    }
    return { valid: true };
  }
}

